import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  Renderer2,
  RendererStyleFlags2,
  OnInit,
  OnDestroy,
  HostListener,
  PLATFORM_ID,
  inject,
  NgZone,
  DOCUMENT
} from '@angular/core';
import {
  Router,
  NavigationEnd,
  NavigationStart,
  Event as RouterEvent,
  RouterOutlet
} from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Subject, combineLatest, takeUntil, debounceTime } from 'rxjs';
import * as DOMPurifyNs from 'dompurify';
import { Application } from '@enums/application.enums';
import { CommonService } from '@modules/shared/services/common.service';
import { API_URLS } from '@core/api-url.enum';
import { AuthService } from '@core/auth.service';
import { ConnectionService } from '@services/connection.service';
import { Fields, HISTORY_NOT_TO_INCLUDE } from '@constants/constant';
import { SeoService } from '@services/seo.service';
import { environment } from '@environments/environment';
import { ApiCallService } from '@services/api-call.service';
import { FirebaseService } from '@modules/shared/services/firebase.service';
import { DataService } from '@services/data.service';
import { imageSize } from '@constants/manifest.constant';
import { HeaderFooterStateService } from '@modules/shared/services/header-footer-state.service';
import { MarketingPageStateService } from '@modules/shared/services/marketing-page-state.service';
import { PromotionalMarketingCleanupService } from '@core/promotional-marketing-cleanup.service';
import { PwaService } from '@core/pwa.service';
import { IndexedDbService } from '@services/indexed-db.service';
import { MobileBridgeService } from '@services/mobile-bridge.service';
import { MOBILE_BRIDGE_EVENT_SEND } from '@constants/mobile-bridge.constant';
import { extractWhiteLabelConfig, isValidWhiteLabelConfig, hasWhiteLabelConfig, isWhiteLabelCoverPagePublished } from '@utils/common.utils';
import { isCoverPageShellPath } from '@modules/sub-page-view/sub-page-view.constants';
import { WLS_PREVIEW_MESSAGE_TYPE } from '@modules/edit-preference/components/whitelabel-site/whitelabel-site-constant';
import { CanonicalService } from '@services/canonical.service';
import { GtagService } from '@services/gtag.service';
import { LocaleService } from '@core/i18n/locale.service';
import { ROUTE_PATHS } from '@constants/routes';
import { AUTH_CONSTANTS } from '@modules/auth/constants/authConstants';
import { UserNotificationService } from '@modules/shared/services/user-notification.service';
import { ThemedDefaultBannerService } from '@services/themed-default-banner.service';
import { APP_SHELL_CONSTANTS, WhiteLabelThemeColors } from './app.constants';
import { HeaderComponent } from './modules/shared/components/header/header.component';
import { SidebarComponent } from './modules/shared/components/sidebar/sidebar.component';
import { FooterComponent } from './modules/shared/components/footer/footer.component';
import { LanguageSwitcherComponent } from '@components/language-switcher/language-switcher.component';
import { SpaLinkDirective } from './modules/shared/directive/spa-link.directive';
import { MinimizedPlayerComponent } from '@components/minimized-player/minimized-player.component';
import { DevToolbarComponent } from '@components/dev-toolbar/dev-toolbar.component';

const appComponentImports = [
  CommonModule,
  RouterOutlet,
  HeaderComponent,
  SidebarComponent,
  FooterComponent,
  LanguageSwitcherComponent,
  SpaLinkDirective,
  MinimizedPlayerComponent,
  ...(environment.production ? [] : [DevToolbarComponent])
];

type DomPurifySanitize = {
  sanitize: (dirty: string, cfg: { ALLOWED_TAGS: string[]; ALLOWED_ATTR: string[] }) => string;
};

const DOMPurify: DomPurifySanitize =
  (DOMPurifyNs as { default?: DomPurifySanitize }).default ??
  (DOMPurifyNs as unknown as DomPurifySanitize);

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: appComponentImports,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly inputSanitize$ = new Subject<{ target: HTMLInputElement | HTMLTextAreaElement }>();
  private readonly authService = inject(AuthService);

  public isBrowser: boolean;
  public showSidebar = false;
  public isWhiteLabelCoverPageVisible = false;
  public isCoverPageShellRoute = false;
  public isSocialCallbackRoute = false;
  public isEmbedWLSMode = false;
  public previewLogoUrl = '';
  public previewTextLogo = '';
  public previewTextLogoFont: string | null = null;
  public previewIsTextLogo = false;
  public readonly showDevToolbar = !environment.production;
  readonly wlsPreviewHeaderLabels = {
    GET_STARTED: AUTH_CONSTANTS.GET_STARTED
  } as const;
  readonly loginPath = ROUTE_PATHS.AUTH_LOGIN;

  private showSidebarForRoute = false;
  private isMarketingPageVisible = false;
  private isSeoLandingPageVisible = false;
  private whitelabelDataFetched = false;
  private sessionIpSynced = false;
  private globalClickListener?: () => void;
  private previewMessageHandler?: (event: MessageEvent) => void;

  get isPwaOrMobileAppShell(): boolean {
    return (
      this.mbs.isMobileApp ||
      (this.isBrowser && !!window.ReactNativeWebView) ||
      this.pwaService.isRunningAsPWA()
    );
  }

  get showCoverPageMobileNav(): boolean {
    if (!this.isWhiteLabelCoverPageVisible || this.isEmbedWLSMode) {
      return false;
    }
    return this.authService.isLogin || this.isPwaOrMobileAppShell;
  }

  get showPwaSidebarImmediate(): boolean {
    if (!this.isPwaOrMobileAppShell || this.isEmbedWLSMode) {
      return false;
    }
    if (this.isMarketingPageVisible || this.isSeoLandingPageVisible) {
      return false;
    }
    const urlPath = this.getUrlPath(this.router.url);
    if (this.isHiddenSidebarPath(urlPath)) {
      return this.isWhiteLabelCoverPageVisible || this.isCoverPageShellRoute;
    }
    return true;
  }

  get showAppSidebar(): boolean {
    return (
      this.showSidebar ||
      this.showCoverPageMobileNav ||
      this.showPwaSidebarImmediate
    );
  }

  @HostListener('document:input', ['$event'])
  onInputEvent(event: Event): void {
    const target = event.target as HTMLTextAreaElement | HTMLInputElement;
    if (this.shouldSanitizeInput(target)) {
      this.inputSanitize$.next({ target });
    }
  }

  @HostListener('document:paste', ['$event'])
  onPasteEvent(event: ClipboardEvent): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (!event.clipboardData || this.shouldSkipPasteSanitization(target)) return;

    event.preventDefault();
    if (target && this.isTextInputElement(target)) {
      const raw = event.clipboardData.getData('text/html') || event.clipboardData.getData('text/plain');
      const cleaned = DOMPurify.sanitize(raw, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
      this.insertTextAtCursor(target, cleaned);
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.isBrowser && event.shiftKey && (event.metaKey || event.key === Fields.Meta)) {
      this.cms.showScreenshotProhibitedModal();
    }
  }

  constructor(
    private readonly router: Router,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly seoService: SeoService,
    private readonly connectionService: ConnectionService,
    private readonly http: HttpClient,
    private readonly apiCallService: ApiCallService,
    private readonly firebaseService: FirebaseService,
    private readonly dataService: DataService,
    private readonly cms: CommonService,
    private readonly headerFooterStateService: HeaderFooterStateService,
    private readonly marketingPageStateService: MarketingPageStateService,
    private readonly promotionalMarketingCleanup: PromotionalMarketingCleanupService,
    private readonly indexedDbService: IndexedDbService,
    private readonly pwaService: PwaService,
    private readonly mbs: MobileBridgeService,
    private readonly ngZone: NgZone,
    private readonly canonicalService: CanonicalService,
    private readonly gtagService: GtagService,
    private readonly localeService: LocaleService,
    private readonly userNotificationService: UserNotificationService,
    private readonly themedDefaultBannerService: ThemedDefaultBannerService
  ) {
    this.initializeEnvironment();
  }

  private initializeEnvironment(): void {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isCoverPageShellRoute = isCoverPageShellPath(this.router.url);
    if (this.isCoverPageShellRoute) {
      this.applyWhiteLabelCoverPageShell(true);
    }
    const bootPath = this.isBrowser
      ? this.document.location?.pathname || this.router.url
      : this.router.url;
    this.syncSocialCallbackChrome(bootPath);
    this.isEmbedWLSMode =
      new URLSearchParams(this.document.location?.search ?? '').get(
        APP_SHELL_CONSTANTS.QUERY.EMBED
      ) === APP_SHELL_CONSTANTS.QUERY.TRUE;
    this.initializeBrowserSpecificFeatures();

    if (hasWhiteLabelConfig()) {
      this.cms.applyWhiteLabelBodyThemeClass();
      this.handleWhiteLabelConfig();
    } else {
      this.clearWhiteLabelOfflineMetadata();
      this.seoService.initializeAppSeo();
      this.setSSRThemeVariables();
    }
    this.processReferentUserDetails();

    if (this.isBrowser) {
      setTimeout(() => this.initializeFirebaseServices());
    }
  }

  private setSSRThemeVariables(customColors?: WhiteLabelThemeColors): void {
    const existingStyle = this.document.getElementById(APP_SHELL_CONSTANTS.STYLE_IDS.SSR_THEME);
    existingStyle?.remove();

    const colors = customColors || {
      bgColor: APP_SHELL_CONSTANTS.FALLBACK_SSR_COLORS.bgColor,
      themeColor: Application.DEFAULT_THEME_COLOR,
      secondaryBgColor: APP_SHELL_CONSTANTS.FALLBACK_SSR_COLORS.secondaryBgColor,
      postTextColor: Application.DEFAULT_TEXT_COLOR,
      buttonTextColor: Application.DEFAULT_BUTTON_TEXT_COLOR,
      bgTextColor: Application.DEFAULT_TEXT_COLOR,
      backgroundImage: APP_SHELL_CONSTANTS.FALLBACK_SSR_COLORS.backgroundImage
    };

    const style = this.document.createElement('style');
    style.id = APP_SHELL_CONSTANTS.STYLE_IDS.SSR_THEME;
    style.textContent = `
      :root {
        --configBgColor: ${colors.bgColor || APP_SHELL_CONSTANTS.FALLBACK_SSR_COLORS.bgColor};
        --configThemeColor: ${colors.themeColor || Application.DEFAULT_THEME_COLOR};
        --configSecondaryBgColor: ${colors.secondaryBgColor || APP_SHELL_CONSTANTS.FALLBACK_SSR_COLORS.secondaryBgColor};
        --configPostTextColor: ${colors.postTextColor || Application.DEFAULT_TEXT_COLOR};
        --configButtonTextColor: ${colors.buttonTextColor || Application.DEFAULT_BUTTON_TEXT_COLOR};
        --configBgTextColor: ${colors.bgTextColor || Application.DEFAULT_TEXT_COLOR};
        --configBackgroundImage: ${colors.backgroundImage ? `url('${colors.backgroundImage}')` : 'none'};
      }
      body {
        background-color: var(--configBgColor);
        color: var(--configPostTextColor);
        transition: ${this.isSocialCallbackRoute ? 'none' : 'background-color 0.3s ease, color 0.3s ease'};
      }
    `;
    this.document.head.appendChild(style);
  }

  private initializeFirebaseServices(): void {
    this.firebaseService.generateFingerprint();
    this.firebaseService.getRemoteConfigValue();
  }

  private handleWhiteLabelConfig(): void {
    const config = extractWhiteLabelConfig();
    if (!config || !isValidWhiteLabelConfig(config)) return;
    this.processWhiteLabelConfig(config);
  }

  private processWhiteLabelConfig(res: any): void {
    if (this.isBrowser && this.document?.location) {
      const urlParams = new URLSearchParams(this.document.location.search);
      if (urlParams.get(APP_SHELL_CONSTANTS.QUERY.PREVIEW) === APP_SHELL_CONSTANTS.QUERY.TRUE) {
        const previewConfig: Record<string, string> = {};
        APP_SHELL_CONSTANTS.PREVIEW_CONFIG_PARAMS.forEach((param) => {
          const value = urlParams.get(param);
          if (value) {
            previewConfig[param] = value;
          }
        });

        if (Object.keys(previewConfig).length > 0) {
          this.authService.isWhiteLabelPreview = true;
          if (res.data?.whiteLabel) {
            Object.assign(res.data.whiteLabel, previewConfig);
          }
        }
      }
    }

    const logo = this.getWhiteLabelLogo(res);
    this.setConfigData(res);
    this.seoService.setWhiteLabelSeo(res.data, res.user, logo);
  }

  private getWhiteLabelLogo(res: any): string {
    const defaultLogo = APP_SHELL_CONSTANTS.DEFAULT_LOGO;
    if (res?.user?.profile_image_url) {
      return res.user.profile_image_url;
    }
    if (res?.data?.whiteLabel?.custom_logo && Object.keys(res.data.whiteLabel.custom_logo).length) {
      return res.data.whiteLabel.custom_logo[imageSize[144]]?.url || defaultLogo;
    }
    return res?.data?.whiteLabel?.logo_url || defaultLogo;
  }

  private processReferentUserDetails(): void {
    if (!this.isBrowser || !this.document?.location) return;
    const urlParams = new URLSearchParams(this.document.location.search);
    if (urlParams.get(APP_SHELL_CONSTANTS.QUERY.IS_EMAIL) !== APP_SHELL_CONSTANTS.QUERY.TRUE) return;

    APP_SHELL_CONSTANTS.REFERENT_PARAMS.forEach((param) => {
      const value = urlParams.get(param);
      if (value) {
        this.cms.referentUserDetails[param] = value;
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.inputSanitize$
      .pipe(debounceTime(APP_SHELL_CONSTANTS.INPUT_SANITIZE_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(({ target }) => {
        const cleaned = DOMPurify.sanitize(target.value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
        if (cleaned !== target.value) {
          target.value = cleaned;
        }
      });

    if (this.isBrowser) {
      this.gtagService.start();
      await this.initializeBrowserServices();
      this.initializeServiceWorker();
      this.themedDefaultBannerService.loadThemedBackgroundImage();
    }
    this.subscribeToServices();
    this.updateSidebarVisibility();
    if (this.authService.isAuthenticated()) {
      this.userNotificationService.start();
    }
  }

  private async initializeBrowserServices(): Promise<void> {
    this.mbs.forceSendMessage(MOBILE_BRIDGE_EVENT_SEND.RELOADED, {});

    const data = await this.indexedDbService.getAllItems();
    this.indexedDbService.setIndexedDb = data;

    this.pwaService.initPwaData();

    if (!this.dataService.getDeviceIp()) {
      setTimeout(() => this.getSystemIPAddress());
    }
  }

  private initializeServiceWorker(): void {
    if (!('serviceWorker' in navigator)) return;

    let reloaded = false;

    navigator.serviceWorker
      .register(APP_SHELL_CONSTANTS.SERVICE_WORKER.SCRIPT, { scope: '/' })
      .catch((error) => console.error('Service Worker registration failed:', error));

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === APP_SHELL_CONSTANTS.SERVICE_WORKER.RELOAD_PAGE && !reloaded) {
        reloaded = true;
        location.reload();
      }
    });
  }

  private subscribeToServices(): void {
    this.connectionService.getConnectionStatus().pipe(takeUntil(this.destroy$)).subscribe();
    this.subscribeClearToasts();
    combineLatest([
      this.marketingPageStateService.isMarketingPageVisible$,
      this.marketingPageStateService.isSeoLandingPageVisible$,
      this.marketingPageStateService.isWhiteLabelCoverPageVisible$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([marketing, seo, cover]) => {
        this.isMarketingPageVisible = marketing;
        this.isSeoLandingPageVisible = seo;
        this.isWhiteLabelCoverPageVisible = cover;
        this.updateCoverPageBodySpacing(cover);
        this.syncSidebarFromFlags();
      });
  }

  private syncSidebarFromFlags(): void {
    this.showSidebar =
      this.showSidebarForRoute &&
      !this.isMarketingPageVisible &&
      !this.isSeoLandingPageVisible &&
      !this.isWhiteLabelCoverPageVisible;
  }

  private updateCoverPageBodySpacing(isCoverPageVisible: boolean): void {
    if (isCoverPageVisible) {
      this.renderer.setStyle(
        this.document.body,
        'padding-top',
        '0',
        RendererStyleFlags2.Important
      );
      return;
    }
    this.renderer.removeStyle(this.document.body, 'padding-top');
  }

  ngOnDestroy(): void {
    this.updateCoverPageBodySpacing(false);
    if (this.isEmbedWLSMode) {
      this.renderer.removeClass(this.document.body, APP_SHELL_CONSTANTS.BODY.WHITE_LABEL);
      this.renderer.removeClass(this.document.body, APP_SHELL_CONSTANTS.BODY.HAS_BACKGROUND_IMAGE);
    }
    this.globalClickListener?.();
    if (this.previewMessageHandler) {
      window.removeEventListener('message', this.previewMessageHandler);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeBrowserSpecificFeatures(): void {
    if (this.isBrowser) {
      this.renderer.addClass(this.document.body, APP_SHELL_CONSTANTS.BODY.HYDRATED);
    }
    this.setupRouterHandling();
    this.setupPreviewMessageListener();
  }

  private setupPreviewMessageListener(): void {
    if (!this.isBrowser) return;

    const urlParams = new URLSearchParams(window.location.search);
    this.isEmbedWLSMode = urlParams.get(APP_SHELL_CONSTANTS.QUERY.EMBED) === APP_SHELL_CONSTANTS.QUERY.TRUE;
    if (!this.isEmbedWLSMode) return;

    this.cms.isWhiteLabelWeb = true;
    this.cms.applyWhiteLabelBodyThemeClass();

    const initialColors = this.extractColorsFromUrlParams(urlParams);
    if (initialColors) {
      this.applyThemeColors(initialColors);
    }

    this.applyPreviewHeaderBranding({
      logo_url: urlParams.get('logo_url') || '',
      user_text: urlParams.get('user_text') || '',
      user_font: urlParams.get('user_font') || '',
      is_text_logo:
        urlParams.get(APP_SHELL_CONSTANTS.QUERY.IS_TEXT_LOGO) ===
        APP_SHELL_CONSTANTS.QUERY.TRUE
    });
    this.headerFooterStateService.updateState({ isHeaderVisible: false });

    this.previewMessageHandler = this.handlePreviewMessage.bind(this);
    window.addEventListener('message', this.previewMessageHandler);
  }

  private extractColorsFromUrlParams(urlParams: URLSearchParams): Record<string, string> | null {
    const colorData: Record<string, string> = {};
    APP_SHELL_CONSTANTS.PREVIEW_COLOR_KEYS.forEach((key) => {
      const value = urlParams.get(key);
      if (value) {
        colorData[key] = value;
      }
    });
    return Object.keys(colorData).length > 0 ? colorData : null;
  }

  private applyThemeColors(data: Record<string, string>): void {
    const elements = [this.document.documentElement, this.document.body];

    Object.entries(APP_SHELL_CONSTANTS.CSS_VAR_MAP).forEach(([dataKey, cssVar]) => {
      if (data[dataKey]) {
        elements.forEach((el) => el.style.setProperty(cssVar, data[dataKey]));
      }
    });

    const bgImage = data.background_image ? `url('${data.background_image}')` : 'none';
    elements.forEach((el) => el.style.setProperty('--configBackgroundImage', bgImage));

    if (data.background_image) {
      this.renderer.addClass(this.document.body, APP_SHELL_CONSTANTS.BODY.HAS_BACKGROUND_IMAGE);
    } else {
      this.renderer.removeClass(this.document.body, APP_SHELL_CONSTANTS.BODY.HAS_BACKGROUND_IMAGE);
    }
  }

  private applyPreviewHeaderBranding(data: Record<string, unknown>): void {
    const siteLogo = String(
      this.cms.getWhiteLabelConfiguration()?.data?.whiteLabel?.logo_url ?? ''
    ).trim();
    const siteText = String(
      this.cms.getWhiteLabelConfiguration()?.data?.whiteLabel?.user_text ?? ''
    ).trim();
    const siteFont = String(
      this.cms.getWhiteLabelConfiguration()?.data?.whiteLabel?.user_font ?? ''
    ).trim();
    const logoUrl = String(data['logo_url'] ?? '').trim() || siteLogo;
    const textLogo = String(data['user_text'] ?? '').trim() || siteText;
    const textFont = String(data['user_font'] ?? '').trim() || siteFont;
    const isTextLogo = data['is_text_logo'] === true && !logoUrl;
    this.previewLogoUrl = isTextLogo ? '' : logoUrl;
    this.previewTextLogo = this.previewLogoUrl ? '' : textLogo;
    this.previewTextLogoFont = textFont || null;
    this.previewIsTextLogo = isTextLogo || !!this.previewTextLogo;
  }

  private handlePreviewMessage(event: MessageEvent): void {
    if (event.data?.type !== WLS_PREVIEW_MESSAGE_TYPE) return;
    const data = event.data.data;
    if (!data) return;

    this.ngZone.run(() => {
      this.applyThemeColors(data);
      this.applyPreviewHeaderBranding(data);
    });
  }

  private setupRouterHandling(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: RouterEvent) => {
      if (event instanceof NavigationStart) {
        this.keepCoverPageShellDuringNavigation(event.url);
        this.syncSocialCallbackChrome(event.url);
        this.onNavigationStart(event);
      }
      if (event instanceof NavigationEnd) {
        this.canonicalService.setCanonicalURL(event.urlAfterRedirects);
        this.handleNavigationEnd(event);
        this.syncSocialCallbackChrome(event.urlAfterRedirects);
        this.recordNavigationHistory(event);
      }
    });
  }

  private keepCoverPageShellDuringNavigation(url: string): void {
    this.isCoverPageShellRoute = isCoverPageShellPath(this.getUrlPath(url));
    if (this.isCoverPageShellRoute) {
      this.showSidebarForRoute = false;
      this.showSidebar = false;
      this.applyWhiteLabelCoverPageShell(true);
    }
  }

  private handleNavigationEnd(event: NavigationEnd): void {
    if (this.isBrowser) {
      window.scrollTo(Application.ZERO, Application.ZERO);
      this.promotionalMarketingCleanup.clearIfStrayMarketingOutsidePublicHome(
        event.urlAfterRedirects
      );
      this.localeService.reapplyTranslation();
    }

    this.cms.setSearchReset(!event.urlAfterRedirects.includes('search-result'));
    this.cms.setGTag('page_view', event.urlAfterRedirects);
    this.updateSidebarVisibility();

    if (this.authService.isAuthenticated()) {
      this.userNotificationService.start();
      if (!this.whitelabelDataFetched) {
        this.whitelabelDataFetched = true;
        this.cms.fetchCreatorWhitelabelData();
      }
      const ip = this.dataService.getDeviceIp();
      if (ip) {
        this.syncLastSessionIfNeeded(ip);
      }
    } else {
      this.whitelabelDataFetched = false;
      this.sessionIpSynced = false;
      this.userNotificationService.stop();
    }

    this.scheduleWhiteLabelBodyThemeResync();
  }

  private scheduleWhiteLabelBodyThemeResync(): void {
    if (!this.isBrowser || !this.shouldApplyWhiteLabelTheme()) {
      return;
    }
    setTimeout(() => {
      if (this.shouldApplyWhiteLabelTheme()) {
        this.cms.applyWhiteLabelBodyThemeClass();
      }
    }, 0);
  }

  private shouldApplyWhiteLabelTheme(): boolean {
    return (
      (this.cms.isWhiteLabelWeb || this.isEmbedWLSMode) &&
      !this.marketingPageStateService.isMarketingPageVisible
    );
  }

  private onNavigationStart(event: NavigationStart): void {
    const isSamePageNavigation = this.getUrlPath(event.url) === this.getUrlPath(this.router.url);
    if (!isSamePageNavigation) {
      this.headerFooterStateService.updateFooterState({ isFooterVisible: false });
    }
    if (this.authService.isAuthenticated()) {
      this.authService.setTokenFor2FA('');
    }
  }

  private recordNavigationHistory(event: NavigationEnd): void {
    const historyPath = event.urlAfterRedirects.split('?')[0];
    if (
      HISTORY_NOT_TO_INCLUDE.includes(event.urlAfterRedirects) ||
      HISTORY_NOT_TO_INCLUDE.includes(historyPath)
    ) {
      return;
    }
    this.authService.history.push(event.urlAfterRedirects);
    if (this.authService.history.length > APP_SHELL_CONSTANTS.HISTORY_LIMIT) {
      this.authService.history.shift();
    }
  }

  private getUrlPath(url: string): string {
    return (url || '').split('?')[0].split('#')[0];
  }

  private isSocialCallbackUrl(url: string): boolean {
    const path = this.getUrlPath(url);
    return path === ROUTE_PATHS.AUTH_SOCIAL_CALLBACK || path.endsWith(ROUTE_PATHS.AUTH_SOCIAL_CALLBACK);
  }

  private syncSocialCallbackChrome(url: string): void {
    const isCallback = this.isSocialCallbackUrl(url);
    this.isSocialCallbackRoute = isCallback;
    if (isCallback) {
      this.headerFooterStateService.updateState({ isHeaderVisible: false });
      this.headerFooterStateService.updateFooterState({ isFooterVisible: false });
    }
    if (this.isBrowser) {
      this.document.documentElement.classList.toggle(
        APP_SHELL_CONSTANTS.BODY.SOCIAL_CALLBACK_BOOT,
        isCallback
      );
    }
  }

  private persistWhiteLabelOfflineMetadata(
    whiteLabel: { user_text?: string } | undefined
  ): void {
    if (!this.isBrowser || !whiteLabel) return;
    const siteName = whiteLabel.user_text?.trim();
    if (siteName) {
      this.cms.setValueInocalStorage(APP_SHELL_CONSTANTS.STORAGE.SITE_NAME, siteName);
    }
  }

  private persistWhiteLabelThemeColors(colors: WhiteLabelThemeColors): void {
    if (!this.isBrowser || !colors || this.authService.isWhiteLabelPreview) return;
    try {
      localStorage.setItem(
        APP_SHELL_CONSTANTS.STORAGE.THEME_COLORS,
        JSON.stringify({
          bg: colors.bgColor,
          theme: colors.themeColor,
          secondary: colors.secondaryBgColor,
          post: colors.postTextColor,
          button: colors.buttonTextColor,
          bgText: colors.bgTextColor
        })
      );
    } catch {
    }
  }

  private clearWhiteLabelOfflineMetadata(): void {
    if (!this.isBrowser) return;
    localStorage.removeItem(APP_SHELL_CONSTANTS.STORAGE.SITE_NAME);
    localStorage.removeItem(APP_SHELL_CONSTANTS.STORAGE.THEME_COLORS);
  }

  private setConfigData(res: any): void {
    this.authService.setIOSThemeMeta(res.data.whiteLabel.bg_color);
    this.cms.setWhiteLabelConfiguration(res);
    this.persistWhiteLabelOfflineMetadata(res.data?.whiteLabel);
    this.cms.applyWhiteLabelBodyThemeClass();

    const colors = this.getWhiteLabelColors(res.data.whiteLabel);
    this.persistWhiteLabelThemeColors(colors);

    if (colors.backgroundImage) {
      this.renderer.addClass(this.document.body, APP_SHELL_CONSTANTS.BODY.HAS_BACKGROUND_IMAGE);
    }

    this.setSSRThemeVariables(colors);
    this.syncInlineThemeVariables(colors);
  }

  /**
   * index.html boot script sets stale colors as inline styles from localStorage; inline
   * styles beat the injected <style>, so overwrite them with the resolved (e.g. preview) colors.
   */
  private syncInlineThemeVariables(colors: WhiteLabelThemeColors): void {
    if (!this.isBrowser) return;
    const values: Record<string, string | undefined> = {
      bg_color: colors.bgColor,
      theme_color: colors.themeColor,
      secondary_bg_color: colors.secondaryBgColor,
      post_text_color: colors.postTextColor,
      button_text_color: colors.buttonTextColor,
      bg_text_color: colors.bgTextColor
    };
    const elements = [this.document.documentElement, this.document.body];
    Object.entries(APP_SHELL_CONSTANTS.CSS_VAR_MAP).forEach(([dataKey, cssVar]) => {
      const value = values[dataKey];
      if (value) {
        elements.forEach((el) => el.style.setProperty(cssVar, value));
      }
    });
    if (colors.bgColor) {
      this.document.body.style.backgroundColor = colors.bgColor;
    }
  }

  private getWhiteLabelColors(whiteLabel: any): WhiteLabelThemeColors {
    return {
      bgColor: whiteLabel.bg_color || (Application.DEFAULT_BG_COLOR as string),
      themeColor: whiteLabel.theme_color || (Application.DEFAULT_THEME_COLOR as string),
      secondaryBgColor: whiteLabel.secondary_bg_color || (Application.DEFAULT_SECONDARY_BG_COLOR as string),
      postTextColor: whiteLabel.post_text_color || (Application.DEFAULT_TEXT_COLOR as string),
      buttonTextColor: whiteLabel.button_text_color || (Application.DEFAULT_BUTTON_TEXT_COLOR as string),
      bgTextColor: whiteLabel.bg_text_color || (Application.DEFAULT_TEXT_COLOR as string),
      backgroundImage: whiteLabel.background_image || null
    };
  }

  private updateSidebarVisibility(): void {
    if (this.isEmbedWLSMode) {
      this.showSidebarForRoute = false;
      this.showSidebar = false;
      return;
    }

    const urlPath = this.getUrlPath(this.router.url);
    this.isCoverPageShellRoute = isCoverPageShellPath(urlPath);

    if (this.isCoverPageShellRoute) {
      this.showSidebarForRoute = false;
      this.showSidebar = false;
      this.applyWhiteLabelCoverPageShell(true);
      return;
    }

    if (
      this.isWhiteLabelCoverPageVisible &&
      hasWhiteLabelConfig() &&
      isWhiteLabelCoverPagePublished()
    ) {
      this.applyWhiteLabelCoverPageShell(false);
    }

    this.showSidebarForRoute = !this.isHiddenSidebarPath(urlPath);
    this.syncSidebarFromFlags();
  }

  private isHiddenSidebarPath(urlPath: string): boolean {
    const extraPaths = this.cms.isWhiteLabelWeb
      ? []
      : APP_SHELL_CONSTANTS.TOPFANS_ONLY_HIDDEN_SIDEBAR_PATHS;

    return (
      [...APP_SHELL_CONSTANTS.HIDDEN_SIDEBAR_PATHS, ...extraPaths].some((route) => {
        const normalizedRoute = route.endsWith('/') ? route.slice(0, -1) : route;
        return urlPath === normalizedRoute || urlPath.startsWith(`${normalizedRoute}/`);
      }) ||
      (urlPath === ROUTE_PATHS.HOME_PATH && !hasWhiteLabelConfig())
    );
  }

  private applyWhiteLabelCoverPageShell(visible: boolean): void {
    if (this.isWhiteLabelCoverPageVisible !== visible) {
      this.isWhiteLabelCoverPageVisible = visible;
      this.marketingPageStateService.setWhiteLabelCoverPageVisible(visible);
    }
    this.updateCoverPageBodySpacing(visible);
    this.headerFooterStateService.updateState({ isHeaderVisible: !visible });
    this.headerFooterStateService.updateFooterState({
      isFooterVisible: !visible
    });
  }

  private subscribeClearToasts(): void {
    if (this.isBrowser) {
      this.globalClickListener = this.renderer.listen('document', 'click', () => {
        this.cms.clearToastMsgs();
      });
    }
  }

  private getSystemIPAddress(): void {
    const existingIp = this.dataService.getDeviceIp();
    if (existingIp) {
      this.syncLastSessionIfNeeded(existingIp);
      return;
    }

    this.http
      .get(environment.IP_ENDPOINT)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const ipAddress = res?.ip || Application.ERROR_IP_ADDRESS;
          this.dataService.setDeviceIP(ipAddress);
          this.syncLastSessionIfNeeded(ipAddress);
        },
        error: () => {
          this.dataService.setDeviceIP(Application.ERROR_IP_ADDRESS);
          this.syncLastSessionIfNeeded(Application.ERROR_IP_ADDRESS);
        }
      });
  }

  private syncLastSessionIfNeeded(ipAddress: string): void {
    if (this.sessionIpSynced || !this.authService.isAuthenticated()) return;
    if (this.authService.userContext?.fm_user) return;
    this.sessionIpSynced = true;
    this.updateLastSession(ipAddress);
  }

  private updateLastSession(ipAddress: string): void {
    const payload = {
      lastSessionIP: ipAddress,
      lastSessionDevice: this.cms.getDeviceInfo()
    };
    this.apiCallService
      .post(API_URLS.sessionUpdate, payload, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Session update failed:', err)
      });
  }

  private shouldSanitizeInput(target: HTMLElement): boolean {
    return !!(
      target &&
      this.isTextInputElement(target) &&
      (target as HTMLInputElement).type !== APP_SHELL_CONSTANTS.INPUT.EMAIL &&
      target.id !== APP_SHELL_CONSTANTS.INPUT.EMBED_LINK
    );
  }

  private shouldSkipPasteSanitization(target: HTMLElement): boolean {
    return !!(
      target &&
      ((target as HTMLInputElement).type === APP_SHELL_CONSTANTS.INPUT.EMAIL ||
        target.id === APP_SHELL_CONSTANTS.INPUT.EMBED_LINK)
    );
  }

  private isTextInputElement(target: HTMLElement): boolean {
    return (
      target.tagName === APP_SHELL_CONSTANTS.INPUT.TEXTAREA ||
      target.tagName === APP_SHELL_CONSTANTS.INPUT.INPUT
    );
  }

  private insertTextAtCursor(target: HTMLInputElement | HTMLTextAreaElement, text: string): void {
    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? 0;
    const value = target.value;
    target.value = value.slice(0, start) + text + value.slice(end);
    target.selectionStart = target.selectionEnd = start + text.length;
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
